/**
 * ListPage — Enhanced list view wrapper.
 *
 * Wraps DynamicTable with PageHeader, optional filter row, and create navigation.
 *
 * Usage:
 * ```tsx
 * <ListPage
 *   collection="employees"
 *   title="Employees"
 *   basePath="/employees"
 *   canCreate={true}
 * />
 * ```
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { DynamicTable } from '../tables/dynamic-table';
import { cn } from '../../lib/utils';
import { Card } from '../ui/card';

// ─── Types ─────────────────────────────────────────────────────────

export interface ListPageProps {
  /** Directus collection name */
  collection: string;
  /** Page title */
  title: string;
  /** Optional icon component */
  icon?: React.ReactNode;
  /** Base path for navigation */
  basePath: string;
  /** Fields to exclude from table */
  excludeFields?: string[];
  /** Maximum columns to show */
  maxColumns?: number;
  /** Initial page size */
  pageSize?: number;
  /** Allow create operations (default: true) */
  canCreate?: boolean;
  /** Additional action buttons in header */
  actions?: React.ReactNode;
  /** Custom filter row (rendered above table) */
  filters?: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Row-level action buttons (Edit, Delete, etc.) */
  rowActions?: (record: any) => React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────

export function ListPage({
  collection,
  title,
  icon,
  basePath,
  excludeFields,
  maxColumns,
  pageSize,
  canCreate = true,
  actions,
  filters,
  className,
  rowActions,
}: ListPageProps) {
  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`${basePath}/${record.id}`);
  };

  const handleAddClick = () => {
    navigate(`${basePath}/create`);
  };

  return (
    <div className={cn('w-full space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon && <div className="text-2xl">{icon}</div>}
          <h1 className="text-3xl font-semibold">{title}</h1>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Filters */}
      {filters && (
        <Card className="p-4">
          {filters}
        </Card>
      )}

      {/* Table */}
      <DynamicTable
        collection={collection}
        title={undefined}
        onRowClick={handleRowClick}
        onAddClick={canCreate ? handleAddClick : undefined}
        excludeFields={excludeFields}
        maxColumns={maxColumns}
        pageSize={pageSize}
        rowActions={rowActions}
      />
    </div>
  );
}
